/**
 * SegmentedToggle: the one segmented toggle for every two-option setting
 * (Regular | After Hours, Active | Discontinued, the composer's kind) and
 * the three-way availability stance (Open | Talk to Me | Not Open).
 *
 * One radiogroup named by `name` (its aria-label) or by a host's visible
 * label through `labelledBy`; two or three radios, each a Button with
 * role="radio" and aria-checked, in the order the options are given. A
 * roving tab stop: the checked radio is the one in the tab order, or the
 * first when none is on (`value` null). Right or Down checks and focuses
 * the next radio, Left or Up the previous, wrapping (radioGroupKeys). A
 * click checks its radio. The group carries `describedBy`, `invalid` and
 * `required` as aria attributes when given, and `title` as its tooltip.
 * While `busy` (a save in flight) the group is aria-busy and each radio
 * aria-disabled, never disabled, so the focus stays where it is; a click
 * or an arrow then does nothing.
 *
 * The shape: a grid of one column per option, so the segments are always
 * one line of equal parts; full width below `sm` (its host's column),
 * content-sized from `sm` up. A label may wrap only between words below
 * `sm`, is one line from `sm` up, and is never truncated. `compact`
 * instead sizes each segment to its own label plus the house compact
 * padding (8px either side, in px as the Button's is) at every width, on
 * one line: a switch that sits in a table row. The base radius on the
 * group and on each segment, never a pill, and no ring. `dense` is
 * `compact` a size smaller (0.75rem labels, 6px either side, a 1.5rem
 * segment): a switch in a busy table row that should give its width to
 * the cells beside it.
 *
 * The colours: a checked segment is the primary Button; with
 * `checkedLook: 'gold'`, the house gold of the Recommended chip over the
 * ghost variant; with `checkedLook: 'terracotta'`, the faded terracotta
 * with ink text over the ghost variant. Unchecked is the quiet ghost on
 * the sand track.
 *
 * An option may be `disabled`: it stays in the group, faded, aria-disabled
 * and never focusable; a click on it does nothing and the arrow keys step
 * over it. A `checkedLook: 'ink'` segment is the ink fill with cream words.
 * Ids: `${testIdPrefix}` is the radiogroup's testid and
 * `${testIdPrefix}-${suffix}` each radio's; with `id`, each radio's id is
 * `${id}-${suffix}`, so a form can move focus to the first one.
 */

import { type KeyboardEvent, type ReactNode, useRef } from 'react';
import { Button } from '@/components/primitives/Button';
import { nextRadioIndex, radioStep } from '@/lib/radioGroupKeys';

export interface SegmentedOption<T extends string | boolean> {
  value: T;
  label: string;
  /** The tail of the radio's testid and id. */
  suffix: string;
  /** The look while checked: the primary Button (default), the house
   *  gold, or the faded terracotta. */
  checkedLook?: 'primary' | 'gold' | 'terracotta' | 'ink';
  /** Shown, faded and aria-disabled; never chosen by a click or a key. */
  disabled?: boolean;
}

export interface SegmentedToggleProps<T extends string | boolean> {
  options: readonly [SegmentedOption<T>, SegmentedOption<T>] | readonly [SegmentedOption<T>, SegmentedOption<T>, SegmentedOption<T>];
  /** null: neither side is on. */
  value: T | null;
  onChange: (next: T) => void;
  /** The group's accessible name (aria-label). */
  name: string;
  /** The id of a visible label that names the group instead. */
  labelledBy?: string;
  /** The ids of the group's help and error lines. */
  describedBy?: string;
  invalid?: boolean;
  required?: boolean;
  testIdPrefix: string;
  id?: string;
  /** Each segment its label plus the compact padding, at every width. */
  compact?: boolean;
  /** compact, a size smaller: 0.75rem labels and 6px either side. */
  dense?: boolean;
  /** Segments side by side while their labels fit on one line each, and
   *  each on its own full-width line when they do not (a phone at 200%
   *  text or page zoom): the text never shrinks and the group never runs
   *  past its column. Full width below `sm`, content-sized from `sm` up. */
  wrap?: boolean;
  /** A save in flight: aria-busy on the group, aria-disabled on each radio. */
  busy?: boolean;
  /** The group's tooltip. */
  title?: string;
}

const TRACK = 'items-stretch rounded-base border border-sage-300 bg-sand p-0.5';

/** One equal column per option, written out whole so Tailwind finds them. */
const GROUP: Record<2 | 3, string> = {
  2: `grid w-full grid-cols-2 ${TRACK} sm:inline-grid sm:w-auto`,
  3: `grid w-full grid-cols-3 ${TRACK} sm:inline-grid sm:w-auto`,
};

const RADIO =
  'min-w-0 w-full text-center leading-tight whitespace-normal break-normal ' +
  '!px-[4px] sm:!px-[14px] sm:whitespace-nowrap';

const COMPACT_GROUP = `inline-flex w-auto ${TRACK}`;

const COMPACT_RADIO = 'text-center leading-tight whitespace-nowrap !px-[8px]';

const DENSE_RADIO = 'text-center leading-tight whitespace-nowrap !min-h-6 !py-0.5 !px-[6px] !text-[0.75rem]';

/** `wrap`: a flex row that breaks onto a new line rather than overflow.
 *  Each segment grows from a zero basis but never below its own label
 *  (min-width: fit-content), so a pair that cannot share a line stacks. */
const WRAP_GROUP = `flex w-full flex-wrap gap-0.5 ${TRACK} sm:inline-flex sm:w-auto`;

const WRAP_RADIO = 'grow basis-0 min-w-fit text-center leading-tight whitespace-nowrap !px-[8px] sm:!px-[14px]';

const GOLD = '!bg-warm-400 !text-forest-700 hover:!bg-warm-400 hover:!text-forest-700 shadow-sh1';

const TERRACOTTA = '!bg-terracotta-soft !text-ink hover:!bg-terracotta-soft hover:!text-ink shadow-sh1';

const INK = '!bg-ink !text-cream hover:!bg-ink hover:!text-cream shadow-sh1';

/** A disabled segment: faded, the arrow cursor, no hover fill. */
const DISABLED = 'opacity-45 !cursor-not-allowed hover:!bg-transparent';

/** A checked segment's classes over the ghost variant, by look; the
 *  primary look is the primary Button itself. */
const CHECKED_LOOK: Record<'gold' | 'terracotta' | 'ink', string> = { gold: GOLD, terracotta: TERRACOTTA, ink: INK };

export function SegmentedToggle<T extends string | boolean>({
  options,
  value,
  onChange,
  name,
  labelledBy,
  describedBy,
  invalid,
  required,
  testIdPrefix,
  id,
  compact = false,
  dense = false,
  wrap = false,
  busy = false,
  title,
}: SegmentedToggleProps<T>): ReactNode {
  const radios = useRef<Array<HTMLButtonElement | null>>([]);
  const checkedIndex = options.findIndex((opt) => value !== null && opt.value === value);

  function onKey(e: KeyboardEvent<HTMLButtonElement>): void {
    const step = radioStep(e.key);
    if (step === 0) return;
    e.preventDefault();
    if (busy) return;
    let next = checkedIndex;
    for (let k = 0; k < options.length; k++) {
      next = nextRadioIndex(next, step, options.length);
      if (!options[next]!.disabled) break;
    }
    if (next === checkedIndex || options[next]!.disabled) return;
    onChange(options[next]!.value);
    radios.current[next]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={name}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-invalid={invalid ? 'true' : undefined}
      aria-required={required ? 'true' : undefined}
      aria-busy={busy ? 'true' : undefined}
      title={title}
      data-testid={testIdPrefix}
      className={compact || dense ? COMPACT_GROUP : wrap ? WRAP_GROUP : GROUP[options.length]}
    >
      {options.map((opt, i) => {
        const checked = i === checkedIndex;
        const tinted =
          opt.checkedLook === 'gold' || opt.checkedLook === 'terracotta' || opt.checkedLook === 'ink' ? opt.checkedLook : null;
        const variant = checked && tinted === null ? 'primary' : 'ghost';
        const look = checked && tinted !== null ? CHECKED_LOOK[tinted] : opt.disabled ? DISABLED : '';
        // With nothing checked the first enabled radio holds the tab stop.
        const firstEnabled = options.findIndex((o) => !o.disabled);
        const tabStop = checkedIndex === -1 ? i === firstEnabled : checked;
        return (
          <Button
            key={opt.suffix}
            variant={variant}
            size="sm"
            role="radio"
            aria-checked={checked}
            aria-disabled={busy || opt.disabled ? 'true' : undefined}
            tabIndex={tabStop ? 0 : -1}
            id={id !== undefined ? `${id}-${opt.suffix}` : undefined}
            data-testid={`${testIdPrefix}-${opt.suffix}`}
            ref={(el) => {
              radios.current[i] = el;
            }}
            className={`${dense ? DENSE_RADIO : compact ? COMPACT_RADIO : wrap ? WRAP_RADIO : RADIO} ${look}`.trim()}
            onClick={() => {
              if (!busy && !opt.disabled) onChange(opt.value);
            }}
            onKeyDown={onKey}
          >
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}
